// @ts-nocheck
/**
 * Variant Gallery Filter
 *
 * Filters the LPO (Live Product Options) Splide gallery thumbnails to show
 * only the images belonging to the currently selected variant's color group.
 *
 * Convention: product images are uploaded in contiguous blocks of GROUP_SIZE
 * per color. Each variant's featured_media marks the first image of its group.
 *
 * Data source: window.__FT_VARIANT_MEDIA_POS  (variantId → 1-based position)
 *              window.__FT_MEDIA_COUNT         (total media count)
 * Both are injected as plain JS globals by product-information.liquid.
 *
 * Event source: listens to the theme's native 'variant:update' event which
 * carries event.detail.resource.id (the new variant ID). This fires after
 * the section fetch completes and is reliable regardless of whether LPO or
 * Shopify native options trigger the change.
 *
 * Nav location: LPO renders the thumbnail nav (.cl-po--gallery-thumbnails)
 * OUTSIDE .cl-po--gallery-wrapper, so we search .product-information__media
 * (the full media column) rather than just the wrapper.
 */
(function () {
  'use strict';

  /** Number of images per color group (confirmed by merchant). */
  var GROUP_SIZE = 5;

  /** CSS class used to collapse out-of-range thumbnail slides. */
  var HIDDEN_CLASS = 'ft-slide--hidden';

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  function getVariantMediaPos() {
    return window.__FT_VARIANT_MEDIA_POS || {};
  }

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  /** Find LPO's thumbnail nav list anywhere in the media column (it lives
   *  outside .cl-po--gallery-wrapper in some LPO versions). */
  function getNavList() {
    // Preferred: within the media column
    var col = document.querySelector('.product-information__media');
    if (col) {
      var list = col.querySelector('.cl-po--gallery-thumbnails .splide__list') ||
                 col.querySelector('.splide--nav .splide__list');
      if (list) return list;
    }
    // Fallback: anywhere on page
    return document.querySelector('.cl-po--gallery-thumbnails .splide__list') ||
           document.querySelector('.splide--nav .splide__list');
  }

  /** Real (non-clone) slides from the thumbnail nav. */
  function getRealNavSlides() {
    var list = getNavList();
    if (!list) return [];
    return Array.from(list.querySelectorAll(':scope > .splide__slide')).filter(function (s) {
      return !s.classList.contains('splide__slide--clone');
    });
  }

  function focusNavIndex(index) {
    var list = getNavList();
    if (!list) return;

    var slides = getRealNavSlides();
    var target = slides[index];
    if (!target) return;
    // Hidden thumbs (`ft-slide--hidden`) have `pointer-events: none`, so the
    // synthetic click silently no-ops. Skip them — caller should be passing
    // a visible target after filterGallery, but defending against races
    // where filter and click overlap.
    if (target.classList.contains(HIDDEN_CLASS)) return;

    // Trigger Splide's native navigation by clicking the target slide.
    // Splide positions slides via CSS transforms (translateX), NOT scrollLeft.
    // Using track.scrollTo/scrollLeft compounds with Splide's transforms and
    // makes the strip lurch. A programmatic click lets Splide handle all
    // positioning through its own sync mechanism.
    target.click();
  }

  /**
   * Directly navigate LPO's main hero carousel to the given 0-based index.
   *
   * `focusNavIndex` clicks a thumbnail and relies on Splide's nav→main sync,
   * but that sync is sometimes skipped for programmatic clicks (LPO may guard
   * against non-user-gesture events). Calling `.go()` on the main Splide
   * instance is the authoritative way to move the hero image regardless of
   * how the event originated.
   */
  function goMainGallery(index) {
    var wrapper = document.querySelector('.product-information .cl-po--gallery-wrapper');
    if (!wrapper) return;
    // The main carousel is any .splide inside the wrapper that is NOT the nav strip.
    var mainEl = wrapper.querySelector('.splide:not(.splide--nav)');
    if (!mainEl || !mainEl.splide) return;
    suppressGalleryLoaderUntil = Date.now() + 250;
    try {
      mainEl.splide.go(index);
    } catch (e) {
      // Guard: Splide not initialised yet or index out of range — silently skip.
    }
  }

  /** Get current variant ID from the URL (?variant=ID). */
  function getVariantIdFromUrl() {
    return new URL(window.location.href).searchParams.get('variant');
  }

  function getSelectedColorValue() {
    var root = document.querySelector('.product-information');
    if (!root) return null;
    var inputs = root.querySelectorAll('input[name="v_Color"]:checked');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (el.closest && el.closest('swatches-variant-picker-component')) continue;
      return el.value ? String(el.value) : null;
    }
    return null;
  }

  /** PDP main product id (buy-buttons / product form), not product-card swatches. */
  function getMainProductId() {
    var el =
      document.querySelector('.product-information product-form-component[data-product-id]') ||
      document.querySelector('.product-information [data-product-id]');
    return el && el.dataset && el.dataset.productId ? String(el.dataset.productId) : null;
  }

  /** After user interacts with the Splide gallery, do not auto-scroll the thumb strip (overrides manual picks). */
  var userGalleryNavUntil = 0;
  var USER_GALLERY_NAV_MS = 12000;

  function markUserGalleryNavigation() {
    userGalleryNavUntil = Date.now() + USER_GALLERY_NAV_MS;
  }

  function shouldAutoScrollThumbs() {
    return Date.now() > userGalleryNavUntil;
  }

  /**
   * Returns true when the user has personalized the product (entered embroidery
   * text), so LPO is rendering the preview canvas on its own slide. In that
   * case, auto-scrolling the main hero to the variant's first image yanks the
   * user away from the personalization preview they want to see updated for the
   * new color. Detection: any LPO text input named like "text" with a value.
   */
  function isLpoPreviewActive() {
    var inputs = document.querySelectorAll('.cl-po--input[type="text"]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (!el.value) continue;
      var name = (el.name || '').toLowerCase();
      if (name.indexOf('text') !== -1) return true;
    }
    return false;
  }

  /**
   * Find the 0-based index of the LPO preview slide in the main carousel
   * (the slide containing `.cl-po--preview`). Returns -1 if not found.
   */
  function findLpoPreviewSlideIndex() {
    var wrapper = document.querySelector('.product-information .cl-po--gallery-wrapper');
    if (!wrapper) return -1;
    var mainEl = wrapper.querySelector('.splide:not(.splide--nav)');
    if (!mainEl) return -1;
    var slides = mainEl.querySelectorAll(':scope .splide__list > .splide__slide:not(.splide__slide--clone)');
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].querySelector('.cl-po--preview')) return i;
    }
    return -1;
  }

  /**
   * Returns true when the main hero carousel is currently showing the LPO
   * preview slide (the user is looking at the personalized canvas, not a
   * variant image).
   */
  function getMainGalleryIndex() {
    var wrapper = document.querySelector('.product-information .cl-po--gallery-wrapper');
    if (!wrapper) return -1;
    var mainEl = wrapper.querySelector('.splide:not(.splide--nav)');
    if (!mainEl || !mainEl.splide || typeof mainEl.splide.index !== 'number') return -1;
    return mainEl.splide.index;
  }

  function isOnLpoPreviewSlide() {
    var wrapper = document.querySelector('.product-information .cl-po--gallery-wrapper');
    if (!wrapper) return false;
    var mainEl = wrapper.querySelector('.splide:not(.splide--nav)');
    if (!mainEl) return false;
    var idx = getMainGalleryIndex();
    if (idx < 0) return false;
    var slides = mainEl.querySelectorAll(':scope .splide__list > .splide__slide:not(.splide__slide--clone)');
    var current = slides[idx];
    return !!(current && current.querySelector('.cl-po--preview'));
  }

  /**
   * Move the main hero only when needed. Skips duplicate go() calls and avoids
   * synthetic thumb clicks (focusNavIndex) that fight LPO and cause 1→2→1 flicker.
   */
  function syncMainGallery(navSlides, targetIdx) {
    if (targetIdx < 0 || targetIdx >= navSlides.length) return;
    var slide = navSlides[targetIdx];
    if (!slide || slide.classList.contains(HIDDEN_CLASS)) return;
    if (getMainGalleryIndex() === targetIdx) return;
    goMainGallery(targetIdx);
  }

  function normalizeToken(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // ---------------------------------------------------------------------------
  // Force-load images in revealed slides
  // ---------------------------------------------------------------------------

  function forceLoadImages(slides) {
    slides.forEach(function (slide) {
      Array.from(slide.querySelectorAll('img')).forEach(function (img) {
        function clearSpinner(reveal) {
          if (reveal) img.style.removeProperty('display');
          var spinner = img.parentElement && img.parentElement.querySelector('.splide__spinner');
          if (spinner) spinner.remove();
        }

        function revealIfLoaded() {
          if (img.complete && img.naturalWidth > 0) clearSpinner(true);
        }

        // Hydrate Splide lazy images.
        // Three distinct cases:
        //   1) No src yet — assign from data-splide-lazy and unhide.
        //   2) Has src but Splide-hidden, AND data-splide-lazy disagrees —
        //      the slide is still in Splide's pre-display state for the
        //      new variant, and the lazy URL is LPO's authoritative new
        //      image. Adopt it to avoid painting the previous color's
        //      stale src (sporadic Bug B).
        //   3) Has src, Splide-hidden, lazy URL agrees (or no lazy URL) —
        //      slide is already loaded for the current variant; just
        //      unhide. Never touch src for a slide the user is currently
        //      looking at (display already removed).
        // IMPORTANT: remove data-splide-lazy after setting src so Splide's
        // own lazy loader doesn't race with us and reset display:none.
        var hasSrc = !!img.getAttribute('src');
        var lazy   = img.dataset.splideLazy || '';
        var hidden = img.style.display === 'none';

        if (lazy && (!hasSrc || (hidden && img.src !== lazy))) {
          img.src = lazy;
          img.removeAttribute('data-splide-lazy');
          img.style.removeProperty('display');
        } else if (hidden && hasSrc) {
          img.style.removeProperty('display');
        } else if (img.dataset.src && (!hasSrc || img.getAttribute('src') === window.location.href)) {
          img.src = img.dataset.src;
          img.style.removeProperty('display');
        } else if (img.getAttribute('loading') === 'lazy' && !img.complete) {
          img.setAttribute('loading', 'eager');
        }

        // If Splide's lazy loader hid the image and the browser already has it cached,
        // the native 'load' event won't fire again. Explicitly reveal and clear spinners.
        revealIfLoaded();

        // Also clear the spinner when the image loads in the non-cached case.
        // Re-register each time (not gated on ftSpinnerLoadHooked) so slides that were
        // reset by Splide after a previous forceLoadImages call get a fresh listener.
        img.addEventListener('load', function onImgLoad() {
          img.removeEventListener('load', onImgLoad);
          clearSpinner(true);
        });
        img.addEventListener('error', function onImgError() {
          img.removeEventListener('error', onImgError);
          clearSpinner(false);
        });
        img.dataset.ftSpinnerLoadHooked = '1';

        // decode() resolves even when the image is cached (and sometimes when "load" won't refire).
        // Use it as another chance to clear the spinner.
        if (typeof img.decode === 'function') {
          try {
            img.decode().then(function () {
              revealIfLoaded();
            }).catch(function () {
              // decode can fail for cross-origin/corrupt images; still remove spinner.
              clearSpinner(false);
            });
          } catch (e) {
            // ignore
          }
        }
      });
    });
  }

  /**
   * Force-hydrate every real (non-clone) main carousel slide. Used the moment
   * LPO injects new variant slides so the very first thumbnail click after a
   * color change lands on a real <img> instead of `display:none` — closes the
   * "must click twice on different angle" race (Bug A).
   */
  function forceLoadMainSlides() {
    var wrapper = document.querySelector('.product-information .cl-po--gallery-wrapper');
    if (!wrapper) return;
    var mainEl = wrapper.querySelector('.splide:not(.splide--nav)');
    if (!mainEl) return;
    var slides = mainEl.querySelectorAll(':scope .splide__list > .splide__slide:not(.splide__slide--clone)');
    if (!slides.length) return;
    forceLoadImages(Array.from(slides));
  }

  /**
   * Hook the main Splide instance's `moved` and `mounted` events so the
   * currently-active slide is always force-hydrated. Fixes Bug A: Splide
   * does `go(index)` even when the target slide still has Splide's own
   * `style="display:none"` lazy placeholder — the hero appears unchanged
   * until a second click. Hydrating on `moved` strips that placeholder
   * the instant the slide becomes active, so the first click always
   * shows the new image.
   *
   * Idempotent — marks the element with `data-ftActiveLoadHooked` so
   * repeated calls (init retry schedule, variant updates) don't stack
   * listeners. Returns true if the hook landed, false if not ready yet
   * or already hooked.
   */
  function hookMainSplideActiveSlideLoad() {
    var wrapper = document.querySelector('.product-information .cl-po--gallery-wrapper');
    if (!wrapper) return false;
    var mainEl = wrapper.querySelector('.splide:not(.splide--nav)');
    if (!mainEl || !mainEl.splide) return false;
    if (mainEl.dataset.ftActiveLoadHooked === '1') return false;
    mainEl.dataset.ftActiveLoadHooked = '1';

    var loadActive = function () {
      try {
        var idx = mainEl.splide.index;
        var slides = mainEl.querySelectorAll(
          ':scope .splide__list > .splide__slide:not(.splide__slide--clone)'
        );
        var current = slides[idx];
        if (current) forceLoadImages([current]);
      } catch (e) { /* Splide may be mid-rebuild — silent skip is correct. */ }
    };

    var onMoved = function () {
      loadActive();
      onGallerySliderChange();
    };

    mainEl.splide.on('moved', onMoved);
    mainEl.splide.on('mounted', function () {
      lastMainGalleryIndex = getMainGalleryIndex();
      loadActive();
    });
    lastMainGalleryIndex = getMainGalleryIndex();
    loadActive(); // hydrate the slide that's active right now
    return true;
  }

  function getVisibleNavSlides() {
    var list = getNavList();
    if (!list) return [];
    return Array.from(list.querySelectorAll(':scope > .splide__slide')).filter(function (s) {
      return !s.classList.contains('splide__slide--clone') &&
        (s.classList.contains('is-visible') || s.classList.contains('is-active') || s.classList.contains('is-next') || s.classList.contains('is-prev'));
    });
  }

  var hydrateTimer;
  function hydrateVisibleThumbs(delay) {
    clearTimeout(hydrateTimer);
    hydrateTimer = setTimeout(function () {
      var slides = getVisibleNavSlides();
      if (!slides.length) return;
      forceLoadImages(slides);
    }, delay == null ? 100 : delay);
  }

  // ---------------------------------------------------------------------------
  // IntersectionObserver: force-load thumbnail images as they scroll into view
  // ---------------------------------------------------------------------------

  var thumbIO;

  function getOrCreateThumbObserver() {
    if (thumbIO) return thumbIO;
    if (typeof IntersectionObserver === 'undefined') return null;
    thumbIO = new IntersectionObserver(function (entries) {
      var slidesToLoad = [];
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        // entry.target is the slide element (not the img) — observing the slide
        // avoids the display:none issue where img is invisible to the IO.
        var slide = entry.target;
        if (!slide.classList.contains('splide__slide--clone')) {
          slidesToLoad.push(slide);
        }
        thumbIO.unobserve(slide);
      });
      if (slidesToLoad.length) {
        // Small defer so any in-flight Splide positioning frame can finish
        // before we mutate img.src / remove data-splide-lazy.
        setTimeout(function () { forceLoadImages(slidesToLoad); }, 50);
      }
    }, { rootMargin: '200px', threshold: 0 });
    return thumbIO;
  }

  function observeThumbImages() {
    var io = getOrCreateThumbObserver();
    if (!io) return;
    var list = getNavList();
    if (!list) return;
    // Observe the SLIDE element, not the img. Splide sets style="display:none"
    // on lazy <img> elements, which makes the IntersectionObserver always
    // report isIntersecting:false for them (display:none elements have zero
    // intersection area). The slide itself has height:100px and no display:none,
    // so the IO correctly fires when it scrolls into the thumbnail strip's
    // visible area.
    list.querySelectorAll(':scope > .splide__slide:not(.splide__slide--clone)').forEach(function (slide) {
      var img = slide.querySelector('img');
      if (img && img.dataset.splideLazy && (!img.getAttribute('src') || img.style.display === 'none')) {
        io.observe(slide);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Core filter logic
  // ---------------------------------------------------------------------------

  function findStartIndex(navSlides, featuredMediaId, pos, colorValue) {
    var id = featuredMediaId != null ? String(featuredMediaId) : null;
    var p = pos != null ? String(pos) : null;
    var color = colorValue != null ? normalizeToken(colorValue) : null;

    // 1) Featured media id — matches the variant’s main image (Shopify/LPO). Must run
    //    before color: color text often matches the second thumb first (e.g. color in
    //    filename) and wrongly drove the gallery to slide 2.
    if (id) {
      for (var i = 0; i < navSlides.length; i++) {
        var slide = navSlides[i];
        if (!slide) continue;

        var direct =
          slide.getAttribute('data-media-id') ||
          slide.getAttribute('slide-id') ||
          slide.getAttribute('data-slide-id') ||
          slide.getAttribute('data-id');
        if (direct && String(direct) === id) return i;

        var match = slide.querySelector(
          '[data-media-id="' + id + '"], [slide-id="' + id + '"], [data-slide-id="' + id + '"], [data-id="' + id + '"]'
        );
        if (match) return i;
      }
    }

    // 2) Catalog media position (1-based), when id isn’t on the thumb DOM.
    if (p) {
      for (var j = 0; j < navSlides.length; j++) {
        var s = navSlides[j];
        if (!s) continue;

        var img = s.querySelector('img[data-media-position], img[data-position], img[data-index]');
        if (img) {
          var imgPos = img.getAttribute('data-media-position') || img.getAttribute('data-position') || img.getAttribute('data-index');
          if (imgPos && String(imgPos) === p) return j;
        }

        var posAttr =
          s.getAttribute('data-media-position') ||
          s.getAttribute('data-position') ||
          s.getAttribute('data-index');
        if (posAttr && String(posAttr) === p) return j;

        var matchPos = s.querySelector(
          '[data-media-position="' + p + '"], [data-position="' + p + '"], [data-index="' + p + '"]'
        );
        if (matchPos) return j;
      }
    }

    // 3) Color token in alt/src — last resort when id/position aren’t findable.
    if (color) {
      for (var c = 0; c < navSlides.length; c++) {
        var cs = navSlides[c];
        if (!cs) continue;
        var cimg = cs.querySelector('img');
        if (!cimg) continue;
        var hay = normalizeToken((cimg.getAttribute('alt') || '') + ' ' + (cimg.getAttribute('src') || ''));
        if (hay && hay.indexOf(color) !== -1) return c;
      }
    }

    return null;
  }

  /**
   * @param {string|null|undefined} variantId
   * @param {string|null|undefined} featuredMediaId
   * @param {string|null|undefined} featuredMediaPos
   * @param {{ syncMain?: boolean }} [options]
   *   syncMain — when true (variant change), move hero to the variant's featured
   *   slide if the current slide was hidden by the filter. When false (page load
   *   retries), only show/hide thumbnails; never programmatically change the hero.
   */
  function filterGallery(variantId, featuredMediaId, featuredMediaPos, options) {
    options = options || {};
    var syncMain = options.syncMain === true;

    var navSlides = getRealNavSlides();
    if (navSlides.length === 0) return false; // LPO not ready yet

    var pos = featuredMediaPos != null ? featuredMediaPos : (variantId != null ? getVariantMediaPos()[String(variantId)] : null);

    // No mapping → show everything
    if (pos == null) {
      return true;
    }

    var startIdx = findStartIndex(navSlides, featuredMediaId, pos, getSelectedColorValue());
    if (startIdx == null) startIdx = Number(pos) - 1; // 1-based → 0-based fallback
    if (startIdx < 0) startIdx = 0;
    var endIdx   = startIdx + GROUP_SIZE - 1;

    // Prefer selecting slides by product media position (more stable than Splide index ordering).
    var startSlideImg = navSlides[startIdx] && navSlides[startIdx].querySelector('img');
    var startPos = (startSlideImg && startSlideImg.getAttribute('data-media-position')) ? Number(startSlideImg.getAttribute('data-media-position')) : Number(pos);
    if (!Number.isFinite(startPos)) startPos = Number(pos);
    // Some thumbnails have alt="null" so the "first color match" can start at position 2 of the block.
    // Include one position before the detected start to capture those edge cases.
    if (Number.isFinite(startPos) && startPos > 1) startPos = startPos - 1;
    var endPos = startPos + GROUP_SIZE - 1;

    var visibleSlides = [];
    var seen = new Set();

    function addSlide(s) {
      if (!s) return;
      if (seen.has(s)) return;
      seen.add(s);
      visibleSlides.push(s);
    }

    // Always include the target slide.
    addSlide(navSlides[startIdx]);

    // 1) Include by index window (fallback).
    navSlides.forEach(function (slide, i) {
      if (i >= startIdx && i <= endIdx) addSlide(slide);
    });

    // 2) Include by media-position window (preferred).
    navSlides.forEach(function (slide) {
      var img = slide && slide.querySelector && slide.querySelector('img[data-media-position]');
      if (!img) return;
      var mp = Number(img.getAttribute('data-media-position'));
      if (Number.isFinite(mp) && mp >= startPos && mp <= endPos) addSlide(slide);
    });

    // Hide slides outside the variant's color group, show slides inside it.
    navSlides.forEach(function (slide) {
      if (seen.has(slide)) {
        slide.classList.remove(HIDDEN_CLASS);
      } else {
        slide.classList.add(HIDDEN_CLASS);
      }
    });

    forceLoadImages(visibleSlides);

    if (!syncMain || !shouldAutoScrollThumbs()) {
      return true;
    }

    // Variant change: jump to featured only when the current hero slide is no longer visible.
    var current = getMainGalleryIndex();
    var currentHidden =
      current < 0 ||
      current >= navSlides.length ||
      !navSlides[current] ||
      navSlides[current].classList.contains(HIDDEN_CLASS);

    var targetIdx = currentHidden ? startIdx : current;
    if (isLpoPreviewActive() && isOnLpoPreviewSlide()) {
      var previewIdx = findLpoPreviewSlideIndex();
      if (previewIdx >= 0) targetIdx = previewIdx;
    }

    requestAnimationFrame(function () {
      syncMainGallery(navSlides, targetIdx);
    });

    return true;
  }

  // ---------------------------------------------------------------------------
  // Main gallery loading indicator
  // ---------------------------------------------------------------------------

  var galleryLoaderTimer;
  /** Skip loader when filterGallery programmatically calls splide.go(). */
  var suppressGalleryLoaderUntil = 0;
  var lastMainGalleryIndex = -1;

  function getGalleryWrapper() {
    return document.querySelector('.product-information .cl-po--gallery-wrapper');
  }

  function showGalleryLoader() {
    var wrapper = getGalleryWrapper();
    if (!wrapper) return;
    wrapper.classList.add('ft-gallery--loading');
    clearTimeout(galleryLoaderTimer);
    // Safety net: always clear after 2.5 s so a missed load event never blocks the UI.
    galleryLoaderTimer = setTimeout(hideGalleryLoader, 2500);
  }

  function hideGalleryLoader() {
    clearTimeout(galleryLoaderTimer);
    var wrapper = getGalleryWrapper();
    if (wrapper) wrapper.classList.remove('ft-gallery--loading');
  }

  /** Dismiss the loader when the active main-carousel slide image is ready. */
  function hookActiveSlideLoaderDismiss() {
    var wrapper = getGalleryWrapper();
    if (!wrapper) return;
    var mainEl = wrapper.querySelector('.splide:not(.splide--nav)');
    if (!mainEl || !mainEl.splide) {
      hideGalleryLoader();
      return;
    }
    var idx = mainEl.splide.index;
    var slides = mainEl.querySelectorAll(
      ':scope .splide__list > .splide__slide:not(.splide__slide--clone)'
    );
    var current = slides[idx];
    if (!current) {
      hideGalleryLoader();
      return;
    }

    var imgs = current.querySelectorAll('img');
    if (!imgs.length) {
      hideGalleryLoader();
      return;
    }

    var waiting = 0;
    imgs.forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) return;
      waiting += 1;
      var settled = false;
      function done() {
        if (settled) return;
        settled = true;
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        waiting -= 1;
        if (waiting <= 0) hideGalleryLoader();
      }
      img.addEventListener('load', done);
      img.addEventListener('error', done);
      if (typeof img.decode === 'function') {
        img.decode().then(function () {
          if (img.complete && img.naturalWidth > 0) done();
        }).catch(done);
      }
    });

    if (waiting === 0) hideGalleryLoader();
  }

  /** User changed the main hero or thumbnail strip (not a programmatic .go()). */
  function onGallerySliderChange() {
    if (Date.now() < suppressGalleryLoaderUntil) return;

    var idx = getMainGalleryIndex();
    if (idx < 0) return;
    if (idx === lastMainGalleryIndex) return;
    lastMainGalleryIndex = idx;

    showGalleryLoader();
    requestAnimationFrame(hookActiveSlideLoaderDismiss);
  }

  function beginUserGallerySlideLoad() {
    showGalleryLoader();
    requestAnimationFrame(function () {
      requestAnimationFrame(hookActiveSlideLoaderDismiss);
    });
  }

  /** Hook load/error on every real main-gallery slide image so the loader is
   *  dismissed as soon as the new variant image finishes loading. */
  function hookGalleryImageLoad() {
    var wrapper = getGalleryWrapper();
    if (!wrapper) return;
    var imgs = wrapper.querySelectorAll('.splide__slide:not(.splide__slide--clone) img');
    imgs.forEach(function (img) {
      if (img.dataset.ftGalleryLoaderHooked) return;
      img.dataset.ftGalleryLoaderHooked = '1';
      img.addEventListener('load', hideGalleryLoader);
      img.addEventListener('error', hideGalleryLoader);
    });
  }

  // ---------------------------------------------------------------------------
  // Event handling
  // ---------------------------------------------------------------------------

  var filterTimerShort;
  var filterTimerLong;
  var lastRequestedVariantId;
  var lastRequestedFeaturedMediaId;
  var lastRequestedFeaturedMediaPos;

  function scheduleFilter(variantId, featuredMediaId, featuredMediaPos, delay, options) {
    lastRequestedVariantId = String(variantId);
    lastRequestedFeaturedMediaId = featuredMediaId != null ? String(featuredMediaId) : null;
    lastRequestedFeaturedMediaPos = featuredMediaPos != null ? String(featuredMediaPos) : null;
    var d = delay == null ? 200 : delay;
    var filterOptions = options || { syncMain: true };

    if (d <= 250) {
      clearTimeout(filterTimerShort);
      filterTimerShort = setTimeout(function () {
        if (String(variantId) !== lastRequestedVariantId) return; // stale
        if (featuredMediaId != null && String(featuredMediaId) !== String(lastRequestedFeaturedMediaId)) return; // stale
        filterGallery(variantId, featuredMediaId, featuredMediaPos, filterOptions);
      }, d);
      return;
    }

    clearTimeout(filterTimerLong);
    filterTimerLong = setTimeout(function () {
      if (String(variantId) !== lastRequestedVariantId) return; // stale
      if (featuredMediaId != null && String(featuredMediaId) !== String(lastRequestedFeaturedMediaId)) return; // stale
      filterGallery(variantId, featuredMediaId, featuredMediaPos, filterOptions);
    }, d);
  }

  /** Theme fires 'variant:update' after the section fetch + morph completes.
   *  event.detail.resource is the variant object with .id. */
  function onVariantUpdate(e) {
    var pid = e && e.detail && e.detail.data && e.detail.data.productId;
    var mainId = getMainProductId();
    if (pid && mainId && String(pid) !== String(mainId)) return;

    var variant = e && e.detail && e.detail.resource;
    var variantId = variant && variant.id ? String(variant.id) : getVariantIdFromUrl();
    if (!variantId) return;
    var featuredMediaId = variant && variant.featured_media && variant.featured_media.id ? String(variant.featured_media.id) : null;
    var featuredMediaPos = variant && variant.featured_media && variant.featured_media.position ? String(variant.featured_media.position) : null;

    // Show the loading indicator immediately; LPO will start swapping the image.
    showGalleryLoader();
    // Give LPO a moment to inject new slide images, then hook load events on them.
    setTimeout(hookGalleryImageLoad, 150);

    // Re-register the active-slide hydration hook in case LPO has rebuilt the
    // main Splide instance. Idempotent — bails fast if already hooked.
    setTimeout(hookMainSplideActiveSlideLoad, 150);

    // Drive forceLoadMainSlides from the timeline directly. The MutationObserver
    // in init() only fires when LPO adds new slide nodes — on the common path
    // LPO just mutates `data-splide-lazy`/`src` on existing slides, so the MO
    // never runs and main slides keep their `display:none` placeholders.
    // Closes Bug A's "must click twice" path for variant changes that don't
    // re-inject the slide DOM.
    [200, 600, 1200, 2000].forEach(function (ms) {
      setTimeout(forceLoadMainSlides, ms);
    });

    // One delayed pass: filter thumbs + sync hero if the current slide was hidden.
    scheduleFilter(variantId, featuredMediaId, featuredMediaPos, 400, { syncMain: true });
  }

  function applyCurrentVariant() {
    // Page load: only filter thumbnails — do not auto-click or .go() the hero.
    filterGallery(getVariantIdFromUrl(), null, null, { syncMain: false });
  }

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------

  function init() {
    // Listen to the theme's post-fetch variant update event (bubbles up to document)
    document.addEventListener('variant:update', onVariantUpdate);

    // Manual gallery / thumbnail clicks: Splide toggles classes on every interaction.
    // Do not treat that as "needs re-sync" by auto-scrolling back to the variant group.
    document.addEventListener(
      'pointerdown',
      function (ev) {
        var media = document.querySelector('.product-information__media');
        if (!media || !media.contains(ev.target)) return;
        if (!ev.target.closest('.splide__slide, .splide__track, .splide__list, .cl-po--gallery-thumbnails')) return;
        markUserGalleryNavigation();
        beginUserGallerySlideLoad();
      },
      true
    );

    // Also listen to URL hash/state changes triggered by LPO
    window.addEventListener('popstate', function () {
      scheduleFilter(getVariantIdFromUrl(), null, null, 400, { syncMain: true });
    });

    // Hydrate only when real (non-clone) slides are injected by LPO on variant change.
    // Splide constantly adds/removes *clone* slides during normal navigation — observing
    // those fires forceLoadImages mid-click, causing layout shifts and src mutations that
    // break the gallery's click-to-navigate behaviour.
    var moRoot =
      document.querySelector('.product-information__media') ||
      document.querySelector('.product-information');
    if (moRoot) {
      var mo = new MutationObserver(function (mutations) {
        var hasNewRealSlides = mutations.some(function (m) {
          return Array.from(m.addedNodes).some(function (n) {
            return n.nodeType === 1 &&
              n.classList.contains('splide__slide') &&
              !n.classList.contains('splide__slide--clone');
          });
        });
        if (hasNewRealSlides) {
          // Hydrate main slides synchronously so the very first thumbnail
          // click after a color change lands on a real <img> rather than
          // a display:none placeholder (the residual Bug A race).
          forceLoadMainSlides();
          // Drop the 150 ms thumb-hydration delay too — same race window.
          hydrateVisibleThumbs(0);
          hookGalleryImageLoad();
          observeThumbImages();
        }
      });
      mo.observe(moRoot, { childList: true, subtree: true });
    }

    // Apply filter for the variant active on page load.
    // LPO loads asynchronously; retry at intervals until slides appear.
    [900, 1800, 3500].forEach(function (ms) {
      setTimeout(applyCurrentVariant, ms);
      setTimeout(observeThumbImages, ms);
      setTimeout(hookMainSplideActiveSlideLoad, ms);
    });

    // Also hydrate thumbs a few times after load (covers slow app mounts).
    [500, 1500, 3000, 6000].forEach(function (ms) {
      setTimeout(function () { hydrateVisibleThumbs(0); }, ms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
 